import React from 'react';
import CallEndIcon from '@mui/icons-material/CallEnd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import VideocamIcon from '@mui/icons-material/Videocam';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import WifiIcon from '@mui/icons-material/Wifi';
import { Avatar, Box, Button, ButtonGroup, Divider, Stack, Typography } from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { usePrivateCall, useSessionContext, useSettings } from '@juno/utils';

const OutgoingCallInner: React.FC = () => {
  const { callState, setCallState } = useSessionContext();
  const { user } = useSettings();
  const { handleCancel, handleJoin, handleJoinInNewTab } = usePrivateCall();
  const theme = useTheme();

  return (
    <motion.div
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
      }}
      initial={{ opacity: 0, bottom: -300 }}
      exit={{ opacity: 0, bottom: -300 }}
      animate={{ opacity: 1, bottom: 80 }}
      transition={{ duration: 0.2 }}
    >
      <Stack
        sx={{
          py: 2,
          px: 2.5,
          background: '#1f1f1f',
          width: 600,
          maxWidth: '95vw',
          height: 290,
          borderRadius: 2,
        }}
        alignItems='center'
      >
        <Typography variant='subtitle2' sx={{ color: '#f4f4f4', pb: 2 }}>
          Join Outgoing Private Call Request
        </Typography>
        <Divider sx={{ borderColor: '#cac4d0', width: '100%' }} />
        <Stack direction='row' alignItems={'center'} sx={{ py: 3 }}>
          <Stack alignItems='center'>
            <Avatar src={user?.avatar || ''} sx={{ height: 100, width: 100, mb: 1.25 }} />
            <Typography variant='subtitle2' sx={{ color: '#f4f4f4' }}>
              You
            </Typography>
          </Stack>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              mx: 8,
            }}
          >
            <PhoneEnabledIcon sx={{ fontSize: 24, transform: 'rotate(90deg)', color: '#f4f4f4' }} />
            <motion.div
              animate={{
                mask: [
                  'radial-gradient(#000 70%,#0000 71%) content-box 50% 100%/0% 0% no-repeat',
                  'radial-gradient(#000 70%,#0000 71%) content-box 50% 100%/100% 100% no-repeat',
                ],
              }}
              transition={{
                duration: 1.5,
                ease: 'easeInOut',
                times: [0, 1],
                repeat: Infinity,
                repeatDelay: 0.3,
              }}
              style={{
                transform: 'rotate(45deg)',
                position: 'absolute',
                top: -10,
                right: -5,
                color: '#f4f4f4',
              }}
            >
              <WifiIcon sx={{ color: '#f4f4f4', fontSize: 22 }} />
            </motion.div>
          </Box>
          <Stack alignItems='center'>
            <Avatar src={callState.otherUser?.avatar} sx={{ height: 100, width: 100, mb: 1.25 }} />
            <Typography variant='subtitle2' sx={{ color: '#f4f4f4' }}>
              {callState.otherUser?.first_name} {callState.otherUser?.last_name}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' gap={2} sx={{ width: '100%' }}>
          <ButtonGroup
            variant='contained'
            aria-label='Join with Audio'
            sx={{
              '& .MuiButtonGroup-grouped': {
                borderColor: darken(theme.palette.success.main, 0.15),
              },
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              color='success'
              fullWidth
              onClick={() => handleJoin(false)}
              aria-label='Accept audio call'
              startIcon={<VolumeUpIcon />}
            >
              Audio
            </Button>
            <Button
              aria-label='Accept audio call in new window'
              variant='contained'
              color='success'
              size='small'
              endIcon={<OpenInNewIcon />}
              sx={{
                '& .MuiButton-endIcon': {
                  marginLeft: 0,
                },
              }}
              onClick={() => handleJoinInNewTab(false)}
            ></Button>
          </ButtonGroup>
          <ButtonGroup
            variant='contained'
            aria-label='Join with Video'
            sx={{
              '& .MuiButtonGroup-grouped': {
                borderColor: darken(theme.palette.success.main, 0.15),
              },
              width: '100%',
            }}
          >
            <Button
              variant='contained'
              color='success'
              fullWidth
              onClick={() => handleJoin(true)}
              aria-label='Accept video call'
              startIcon={<VideocamIcon />}
            >
              Video
            </Button>
            <Button
              aria-label='Accept video call in new window'
              variant='contained'
              color='success'
              size='small'
              endIcon={<OpenInNewIcon />}
              sx={{
                '& .MuiButton-endIcon': {
                  marginLeft: 0,
                },
              }}
              onClick={() => handleJoinInNewTab(true)}
            ></Button>
          </ButtonGroup>
          <Button
            variant='contained'
            color='error'
            fullWidth
            onClick={() => {
              handleCancel();
              setCallState({});
            }}
            aria-label='Decline call'
            startIcon={<CallEndIcon />}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </motion.div>
  );
};

const OutgoingCall: React.FC = () => {
  const { callState } = useSessionContext();
  return (
    <AnimatePresence>
      {callState.sessionId &&
        callState.isCaller &&
        !callState.dialogOpen &&
        !callState.hasJoined && <OutgoingCallInner />}
    </AnimatePresence>
  );
};

export default OutgoingCall;
